import { getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import { formatMinutesToHoursString } from "@components/inputs/date/utils";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IReportHierarchy } from "@components/smart/smartTable";
import { TCellValue } from "@components/table";
import { EntityTypeName } from "@odata/GeneratedEntityTypes";
import { PrAbsenceCategoryTypeCode } from "@odata/GeneratedEnums";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import { IFormatOptions } from "@odata/OData.utils";
import { CommonReportProps } from "@pages/reports/CommonDefs";
import {
    getEmployeeNumberOursCommonDef,
    getPayslipCommonDef,
    payslipInitialSortBy
} from "@pages/reports/payrollOverview/PayrollOverview.utils";
import { setReportDef } from "@pages/reports/Report.test.utils";
import { isDefined, roundToDecimalPlaces } from "@utils/general";
import i18next from "i18next";

import { DASH_CHARACTER } from "../../../constants";
import { FieldType } from "../../../enums";
import { TValue } from "../../../global.types";
import { ROUTE_EMPLOYMENT } from "../../../routes";
import DateType, { DateFormat, getUtcDate } from "../../../types/Date";
import NumberType from "../../../types/Number";
import { IReportTableDefinition, TReportColumnOverrides } from "../Report.utils";
import { ReportId } from "../ReportIds";

const vacationAndSickdayTimeFormatter = (val: TValue, args: IFormatOptions): TCellValue => {
    if (!isDefined(val) || isNaN(Number(val))) {
        return DASH_CHARACTER;
    }
    const numVal = Number(val);
    const type = args.entity["PrVacationAndSickDaysReportView_Type"] as PrAbsenceCategoryTypeCode.SickDay | PrAbsenceCategoryTypeCode.Dovolená;
    const minutesPerDay = args.entity["PrVacationAndSickDaysReportView_MinutesPerDay"];

    if (type === PrAbsenceCategoryTypeCode.SickDay) {
        return `${NumberType.format(numVal)} ${i18next.t("Common:General.Day")}`;
    } else if (type === PrAbsenceCategoryTypeCode.Dovolená) {
        const days = roundToDecimalPlaces(1, (numVal / minutesPerDay));
        return `${formatMinutesToHoursString(Number(val))} (${NumberType.format(days)} ${i18next.t("Common:General.Day")})`;
    }
    return val.toString();
};

const absenceTypeFormatter = (val: TValue, args: IFormatOptions): TCellValue => {
    if (!isDefined(val)) {
        return null;
    }
    console.log("absence type", val, getEnumDisplayValue(EntityTypeName.PrAbsenceCategoryType, val as PrAbsenceCategoryTypeCode));
    return getEnumDisplayValue(EntityTypeName.PrAbsenceCategoryType, val as PrAbsenceCategoryTypeCode);
};

export const getDefinition = (): IReportTableDefinition => {
    const tableId = ReportId.VacationAndSickDaysOverview;
    const title = i18next.t("Reporting:VacationAndSickDaysOverview.Title");
    const path = "VacationAndSickDaysOverview";
    
    const filterBarDef: IFilterGroupDef[] = [{
        ...getDefaultFilterGroupDef(FilterBarGroup.Parameters),
        defaultFilters: [
            CommonReportProps.byDate
        ],
        filterDefinition: {
            [CommonReportProps.byDate]: {
                type: FieldType.MonthYear,
                label: i18next.t("Reporting:VacationAndSickDaysOverview.Date"),
                defaultValue: getUtcDate(),
                formatter: (val) => {
                    return DateType.format(val as Date, DateFormat.monthAndYear);
                }
            }
        },
    }, {
        ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
        allowCustomFilters: false,
        defaultFilters: [],
        filterDefinition: {}
    }];

    const parameters: string[] = [CommonReportProps.byDate];
    const defaultReportHierarchy: IReportHierarchy = {
        "Aggregate": false,
        "Groups": [],
        "Columns": [
            {
                "ColumnAlias": "YearMonth",
            },
            {
                "ColumnAlias": "PrEmployment_NumberOurs",
            },
            {
                "ColumnAlias": "PrEmployment_Name",
            },
            {
                "ColumnAlias": "PrEmployee_NumberOurs",
            },
            {
                "ColumnAlias": "Name",
            },
            {
                "ColumnAlias": "PrVacationAndSickDaysReportView_Type",
            },
            {
                "ColumnAlias": "PrVacationAndSickDaysReportView_Used",
            },
            {
                "ColumnAlias": "PrVacationAndSickDaysReportView_Entitlement",
            },
            {
                "ColumnAlias": "PrVacationAndSickDaysReportView_CarriedOverFromLastYear",
            },
            {
                "ColumnAlias": "PrVacationAndSickDaysReportView_Left",
            },
            {
                "ColumnAlias": "PrVacationAndSickDaysReportView_RealEntitlement",
            }
        ],
        "Aggregations": []
    };


    const columnOverrides: TReportColumnOverrides = {
        YearMonth: getPayslipCommonDef(),
        PrEmployee_NumberOurs: getEmployeeNumberOursCommonDef(),
        PrEmployment_NumberOurs: {
            formatter: (val: TValue, args: IFormatOptions): TCellValue => {
                if (!val) {
                    return null;
                }
                const employmentId = args.entity["PrEmployment_Id"];
                if (!employmentId) {
                    return val.toString();
                }
                const url = `${ROUTE_EMPLOYMENT}/${employmentId}`;
                return getTableIntentLink(val.toString(), {
                    route: url,
                    context: args.storage.context,
                    storage: args.storage
                });
            }
        },
        PrVacationAndSickDaysReportView_Type: {
            formatter: absenceTypeFormatter
        },
        PrVacationAndSickDaysReportView_Used: {
            formatter: vacationAndSickdayTimeFormatter
        },
        PrVacationAndSickDaysReportView_Entitlement: {
            formatter: vacationAndSickdayTimeFormatter
        },
        PrVacationAndSickDaysReportView_CarriedOverFromLastYear: {
            formatter: vacationAndSickdayTimeFormatter
        },
        PrVacationAndSickDaysReportView_Left: {
            formatter: vacationAndSickdayTimeFormatter
        },
        PrVacationAndSickDaysReportView_RealEntitlement: {
            formatter: vacationAndSickdayTimeFormatter
        }
    };

    const initialSortBy = payslipInitialSortBy;


    return {
        title, path, id: tableId, initialSortBy, columnOverrides, filterBarDef, parameters, defaultReportHierarchy
    };
};

setReportDef(ReportId.VacationAndSickDaysOverview, getDefinition);